/**
 * Member revenue chart model — card 4530 (BOARD.md).
 *
 * WHY THIS IS A `.js` AND NOT PART OF THE COMPONENT. `jest.config.js` on this tree runs
 * `transform: {}` with `moduleFileExtensions: ['js','mjs','json']`, so a `.jsx` module is not
 * resolvable by jest at all — any helper left inside `MemberRevenueChart.jsx` would be
 * permanently untestable. Every pure derivation therefore lives here, exactly as
 * `web/utils/executiveChartModel.js` does for `ExecutiveCharts.jsx`, and the `.jsx` stays a thin
 * renderer that performs NO arithmetic of its own.
 *
 * ⛔ AND THIS IS WHY THE HOUSE `formatMoney`/`formatCount` ARE NOT IMPORTED: they are exported
 * from `web/components/admin/ExecutiveKpiRow.jsx` — a `.jsx`, so importing them would drag this
 * module back out of jest's reach. `formatPrice` (`web/utils/formatters.js`) IS a `.js` and is
 * reused rather than re-implemented.
 *
 * INPUT. The payload built by `api/services/analytics/memberRevenue.js` (card 4528) and served
 * by `GET /api/admin/analytics/member-revenue` (card 4529):
 *
 *   { asOf, scope: { activeMembers, excludedByReporting },
 *     chunks: [{ key, label, planCode, agencyName, kind, activeMembers,
 *                expectedMonthlyCents, sources: {…5 keys…} }],
 *     totals: { activeMembers, expectedMonthlyCents, sources: {…5 keys…} },
 *     collected?: { settledCents, settledCount, outstandingCents, openCount,
 *                   instrumentedMembers, source, window: { since, until } } }
 *
 * 🔑 ALL MONEY ARRIVES AS INTEGER CENTS; the view formats. `expectedMonthlyDollars` exists only
 * because recharts plots a number — it is derived once, here, so the component never divides.
 *
 * 🪤 EVERY ABSENCE TEST GOES THROUGH `toFiniteOrNull`, NEVER THROUGH TRUTHINESS OR `Number()`.
 * `Number(null)` and `Number('')` are both **0**, which is the precise coercion trap card 4528
 * recorded twice (a `Number(x) === 0` probe matched 114 rows instead of 1). A chunk legitimately
 * worth 0 cents — a comp tier — must render as $0.00, never as absent, and an absent field must
 * never render as $0.00.
 *
 * 🔴 THE TOTAL IS PUBLISHED, NEVER RE-DERIVED. `toSummaryRows` renders
 * `totals.expectedMonthlyCents` verbatim and reports the chunk sum alongside it as
 * `chunkSumCents`/`reconciles`. Silently re-deriving the total from the chunks would make a
 * server-side accounting disagreement invisible — the one thing a money surface must not do.
 */
import { formatPrice } from './formatters.js';

/** Rendered where a value is genuinely absent. Punctuation, not copy; no emojis (STANDARDS). */
export const EMPTY_VALUE = '—';

/** The synthetic key/label of the summary's last row. */
export const MEMBER_REVENUE_TOTAL_KEY = '__total__';
export const MEMBER_REVENUE_TOTAL_LABEL = 'Total Member Revenue';

/**
 * The five ladder rungs in precedence order — the same list, in the same order, as
 * `MEMBER_REVENUE_SOURCES` in the service. Ordering is load-bearing: it breaks ties in
 * `resolveDominantSource` toward the stronger evidence of price.
 */
export const MEMBER_REVENUE_SOURCE_ORDER = Object.freeze([
  'contracted',
  'custom_tier',
  'custom_tier_via_plan',
  'list_price',
  'estimated',
  'unresolved'
]);

/**
 * Rung → badge. `custom_tier_via_plan` collapses onto the `custom_tier` BADGE because it is the
 * same economic rung reached by a second door (a `custom_<uuid>` plan string), and the card
 * specifies four badge values. ⛔ The collapse is display-only: `dominantSource` keeps the raw
 * rung and `sources` is passed through untouched, so nothing is hidden from a consumer or a test.
 */
export const MEMBER_REVENUE_SOURCE_BADGES = Object.freeze({
  contracted: 'contracted',
  custom_tier: 'custom_tier',
  custom_tier_via_plan: 'custom_tier',
  list_price: 'list_price',
  estimated: 'estimated/guessed',
  unresolved: 'unresolved'
});

const isNil = (value) => value === null || value === undefined;

/**
 * The house absence test, copied rather than imported (its home is a `.jsx`). Empty string and
 * nullish are ABSENT — `Number('')` and `Number(null)` are 0 and would otherwise fabricate a price.
 */
export const toFiniteOrNull = (value) => {
  if (isNil(value) || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Absent stays absent; a real 0 stays 0. */
const toFiniteOrZero = (value) => toFiniteOrNull(value) ?? 0;

const readChunks = (payload) => (Array.isArray(payload?.chunks) ? payload.chunks : []);
const readMonthlySeries = (payload) =>
  (Array.isArray(payload?.monthlySeries) ? payload.monthlySeries : []);

/** Pure. Integer cents → a dollars NUMBER for the chart axis. `null` when absent. */
export const convertCentsToDollars = (cents) => {
  const c = toFiniteOrNull(cents);
  return c === null ? null : c / 100;
};

/** Pure. Cents → `$46,790.25`. Two decimals always — this is the accounting form. */
export const formatMemberRevenueMoney = (cents) => {
  const c = toFiniteOrNull(cents);
  return c === null ? EMPTY_VALUE : formatPrice(c);
};

/**
 * Pure. Cents → `$46,790`, thousands separated, NO cents. The axis-tick and bar-label form:
 * two decimals on every bar label makes a 13-bar chart unreadable, and the exact figure is one
 * row away in the summary. ⛔ Never use this where the number is the accounting answer.
 */
export const formatMemberRevenueCompact = (cents) => {
  const c = toFiniteOrNull(cents);
  return c === null ? EMPTY_VALUE : `$${Math.round(c / 100).toLocaleString('en-US')}`;
};

/** Pure. Whole-number headcount, thousands separated. */
export const formatMemberRevenueCount = (value) => {
  const n = toFiniteOrNull(value);
  return n === null ? EMPTY_VALUE : n.toLocaleString('en-US');
};

/**
 * Pure. Tick formatter for the DOLLARS axis, whose values are already dollars (recharts is handed
 * `expectedMonthlyDollars`). It lives here rather than in the component so the `.jsx` never has to
 * convert a unit — the whole reason this module exists.
 */
export const formatMemberRevenueDollarTick = (dollars) => {
  const d = toFiniteOrNull(dollars);
  return d === null ? EMPTY_VALUE : `$${Math.round(d).toLocaleString('en-US')}`;
};

/**
 * Pure. The rung that priced most of a chunk's members, or `null` when the histogram is empty
 * or all-zero. Ties resolve toward `MEMBER_REVENUE_SOURCE_ORDER` — i.e. toward the stronger
 * evidence of price — because `reduce` only replaces on a STRICTLY greater count.
 */
export const resolveDominantSource = (sources) => {
  const best = MEMBER_REVENUE_SOURCE_ORDER
    .map((key) => ({ key, count: toFiniteOrZero(sources?.[key]) }))
    .reduce((held, next) => (next.count > held.count ? next : held), { key: null, count: 0 });
  return best.count > 0 ? best.key : null;
};

/** Pure. Does this payload have anything to plot? Drives a REAL empty state, not a render guard. */
export const hasMemberRevenueData = (payload) => readMonthlySeries(payload).length > 0;

/**
 * Pure. One row per chunk, carrying BOTH series plus every pre-formatted string the renderer
 * needs, so the component performs no arithmetic and no formatting of its own.
 */
const toRevenueRow = (chunk, { monthly = false } = {}) => {
    const cents = toFiniteOrZero(chunk?.expectedMonthlyCents);
    const members = toFiniteOrZero(chunk?.activeMembers);
    const dominantSource = resolveDominantSource(chunk?.sources);
    return {
      key: chunk?.key ?? chunk?.month ?? null,
      label: chunk?.label ?? chunk?.month ?? 'Unknown',
      month: chunk?.month ?? null,
      planCode: monthly ? null : chunk?.planCode ?? null,
      agencyName: monthly ? null : chunk?.agencyName ?? null,
      kind: monthly ? 'month' : chunk?.kind ?? null,
      sources: monthly ? null : chunk?.sources ?? null,
      dominantSource,
      sourceBadge: monthly || isNil(dominantSource)
        ? null
        : MEMBER_REVENUE_SOURCE_BADGES[dominantSource] ?? dominantSource,
      activeMembers: members,
      expectedMonthlyCents: cents,
      expectedMonthlyDollars: convertCentsToDollars(cents),
      money: formatMemberRevenueMoney(cents),
      moneyCompact: formatMemberRevenueCompact(cents),
      membersLabel: formatMemberRevenueCount(members)
    };
};

export const toChartRows = (payload) =>
  readMonthlySeries(payload).map((row) => toRevenueRow(row, { monthly: true }));

/** Pure. The chunk sum — reported BESIDE the published total, never substituted for it. */
export const sumChunkCents = (payload) =>
  readChunks(payload).reduce((acc, chunk) => acc + toFiniteOrZero(chunk?.expectedMonthlyCents), 0);

/**
 * Pure. The summary table: one row per chunk, then `Total Member Revenue` LAST.
 *
 * 🔴 The total row's `expectedMonthlyCents` is `totals.expectedMonthlyCents` VERBATIM. When the
 * chunks disagree with it, `reconciles` goes false and `chunkSumCents` carries the other number —
 * the disagreement is published, ⛔ not silently repaired by re-deriving the total.
 */
export const toSummaryRows = (payload) => {
  const totalCents = toFiniteOrNull(payload?.totals?.expectedMonthlyCents);
  const totalMembers = toFiniteOrNull(payload?.totals?.activeMembers);
  const chunkSumCents = sumChunkCents(payload);
  return [
    ...readChunks(payload).map((row) => ({ ...toRevenueRow(row), isTotal: false })),
    {
      key: MEMBER_REVENUE_TOTAL_KEY,
      label: MEMBER_REVENUE_TOTAL_LABEL,
      isTotal: true,
      planCode: null,
      agencyName: null,
      sourceBadge: null,
      expectedMonthlyCents: totalCents,
      activeMembers: totalMembers,
      money: formatMemberRevenueMoney(totalCents),
      moneyCompact: formatMemberRevenueCompact(totalCents),
      membersLabel: formatMemberRevenueCount(totalMembers),
      chunkSumCents,
      chunkSumMoney: formatMemberRevenueMoney(chunkSumCents),
      reconciles: totalCents !== null && chunkSumCents === totalCents
    }
  ];
};

/**
 * Pure. The published total row out of `toSummaryRows`, found by its `isTotal` flag rather than
 * by index — the renderer must never do index arithmetic to locate the number it is publishing.
 * (That the row is also LAST is a separate property, asserted by the suite.)
 */
export const readTotalRow = (summaryRows) =>
  (Array.isArray(summaryRows) ? summaryRows : []).find((row) => row?.isTotal === true) ?? null;

/**
 * Pure. The honesty line (REQ-08). It is built unconditionally and rendered unconditionally —
 * a line that appears only when non-zero is a line nobody learns to look for, so `0 unresolved`
 * is a real, screenshot-able reading rather than an absence.
 */
export const toUnresolvedNotice = (payload) => {
  const unresolvedMembers = toFiniteOrZero(payload?.totals?.sources?.unresolved);
  const listPriceMembers = toFiniteOrZero(payload?.totals?.sources?.list_price);
  const estimatedMembers = toFiniteOrZero(payload?.totals?.sources?.estimated);
  return {
    unresolvedMembers,
    listPriceMembers,
    estimatedMembers,
    text: `${formatMemberRevenueCount(estimatedMembers)} members assigned an estimated Individual, Spouse, or Family rate — guessed from household evidence and clearly labeled; ${formatMemberRevenueCount(unresolvedMembers)} members unresolved — no contracted, plan, or estimated price found; excluded from the revenue total`,
    fallbackText: `${formatMemberRevenueCount(listPriceMembers)} priced from the list-price fallback`
  };
};

/**
 * Pure. REQ-10's two stats. They answer different questions over different populations:
 * `expected` is a contracted monthly RATE with no window; `collected` is cash actually settled in
 * a window, instrumented for only a fraction of members. ⛔ They are never summed and never
 * netted — this returns them as two separately labelled blocks and nothing that combines them.
 *
 * `collected` is absent until card 4529 serves it; `available: false` is what the renderer shows,
 * ⛔ never a $0.00 that would read as "we collected nothing".
 */
export const toExpectedCollected = (payload) => {
  const expectedCents = toFiniteOrNull(payload?.totals?.expectedMonthlyCents);
  const collected = payload?.collected ?? null;
  const settledCents = toFiniteOrNull(collected?.settledCents);
  const instrumentedMembers = toFiniteOrNull(collected?.instrumentedMembers);
  return {
    expected: {
      label: 'Expected monthly (contracted)',
      cents: expectedCents,
      money: formatMemberRevenueMoney(expectedCents)
    },
    collected: {
      label: isNil(instrumentedMembers)
        ? 'Collected month-to-date'
        : `Collected month-to-date (${formatMemberRevenueCount(instrumentedMembers)} members instrumented)`,
      cents: settledCents,
      money: isNil(collected) ? EMPTY_VALUE : formatMemberRevenueMoney(settledCents),
      instrumentedMembers,
      available: !isNil(collected),
      shouldDisplay: !isNil(collected) && settledCents > 0,
      window: collected?.window ?? null
    }
  };
};

export default {
  EMPTY_VALUE,
  MEMBER_REVENUE_TOTAL_KEY,
  MEMBER_REVENUE_TOTAL_LABEL,
  MEMBER_REVENUE_SOURCE_ORDER,
  MEMBER_REVENUE_SOURCE_BADGES,
  toFiniteOrNull,
  convertCentsToDollars,
  formatMemberRevenueMoney,
  formatMemberRevenueCompact,
  formatMemberRevenueCount,
  formatMemberRevenueDollarTick,
  resolveDominantSource,
  hasMemberRevenueData,
  toChartRows,
  sumChunkCents,
  toSummaryRows,
  readTotalRow,
  toUnresolvedNotice,
  toExpectedCollected
};
